import { useCallback, useState } from 'react';
import { toast } from 'sonner';
import { discoverDataSources, discoverCrossOrgDataSources, exploreSchema } from '@/services/api/dataDiscoveryService';
import { getImportedSchemaBinding, type CsvDatasetImportResult } from '@/services/api/csvDatasetImportApi';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import type { QueryTemplate, TemplateQuery } from '@/services/api/queryTemplateApi';
import type {
  ChartDataSource,
  DataDiscoveryResult,
  SchemaFieldInfo,
  SchemaPreview,
  SchemaExplorationResult,
  TemplateFieldSelection,
  ChartQueryDefinition,
  ChartMeasure,
  TimeGranularity,
} from '@/types/chartDataSource';
import { getXAxisKeyFromComposite, getLineKeysFromComposite } from '@/hooks/useQueryTemplateData';
import type { Component, TableColumn } from '@/types';
import { ComponentType } from '@/types';
import type { PinDataBinding } from '@/components/charts/MapComponent.types';
import { isMapChartComponent } from '@/types/component-properties';
import type { MapPin as ComponentMapPin } from '@/types/component-properties';
import {
  COMPOSITE_SOURCE_OWNER_MISMATCH_ERROR,
  getCompositeDefinitionOrganizationScope,
  PERSONAL_SPACE_COMPOSITE_SCOPE_ERROR,
  validateCompositeDefinitionSourceOwners,
  type CompositeBindingSelection,
} from './compositeScope';

type BindableMapPin = ComponentMapPin & {
  dataBindings?: PinDataBinding[];
  name?: string;
};

interface UseDataDiscoveryOptions {
  component: Component | null | undefined;
  updateComponent: (id: string, updates: Partial<Component>) => void;
  onMessage: (content: string) => void;
  onSetInput: (value: string) => void;
  onUpdateLinkedDataText?: (chartId: string, valueFields: string[], dateField: string) => void;
}

function getComponentPins(component: Component): BindableMapPin[] {
  if (!isMapChartComponent(component)) {
    return [];
  }

  return Array.isArray(component.properties.pins) ? component.properties.pins : [];
}

function getPinDataBindings(pin: BindableMapPin): PinDataBinding[] {
  return Array.isArray(pin.dataBindings) ? pin.dataBindings : [];
}

function getDataSourceOrganizationFields(
  organizationId?: string,
  organizationName?: string,
): Pick<ChartDataSource, 'organizationId' | 'organizationName'> {
  if (!organizationId) return {};
  return organizationName ? { organizationId, organizationName } : { organizationId };
}

const MULTI_SERIES_CHART_TYPES = new Set<ComponentType>([
  ComponentType.AREA_CHART,
  ComponentType.BAR_CHART,
  ComponentType.LINE_CHART,
  ComponentType.SCATTER_CHART,
]);

function isTableComponentType(type: ComponentType): boolean {
  return type === ComponentType.TABLE || type === ComponentType.ADVANCED_TABLE;
}

function getSchemaFieldsForTabularBinding(
  explorationResult: SchemaExplorationResult,
): SchemaFieldInfo[] {
  const fieldsByName = new Map<string, SchemaFieldInfo>();
  const addField = (field: SchemaFieldInfo) => {
    if (!fieldsByName.has(field.name)) {
      fieldsByName.set(field.name, field);
    }
  };

  explorationResult.schema.fields.forEach(addField);
  explorationResult.timeFields.forEach(addField);
  explorationResult.chartableFields.forEach(addField);
  explorationResult.otherFields.forEach(addField);

  return Array.from(fieldsByName.values());
}

function createRawTableMeasures(fields: SchemaFieldInfo[]): ChartMeasure[] {
  return fields.map((field) => ({
    field: field.name,
    aggregation: 'none',
    alias: field.name,
  }));
}

function createTableColumnsFromSchemaFields(fields: SchemaFieldInfo[]): TableColumn[] {
  return fields.map((field) => ({
    id: field.name,
    accessorKey: field.name,
    header: field.displayName || field.name,
    enableSorting: true,
    enableColumnFilter: true,
  }));
}

function getCompositeLineKeysForComponent(
  component: Component,
  compositeDefinition: NonNullable<TemplateFieldSelection['compositeQueryDefinition']>,
  selectedField: string,
  xAxisKey: string,
): string[] | undefined {
  if (MULTI_SERIES_CHART_TYPES.has(component.type)) {
    return getLineKeysFromComposite(compositeDefinition);
  }

  return selectedField !== xAxisKey ? [selectedField] : undefined;
}

function getQueryOrganizationFields(
  organizationId?: string,
  organizationName?: string,
): Pick<ChartQueryDefinition, 'organizationId' | 'organizationName'> {
  if (!organizationId) return {};
  return organizationName ? { organizationId, organizationName } : { organizationId };
}

function resolveOrganizationName(
  organizationId?: string,
): string | undefined {
  if (!organizationId) {
    return undefined;
  }

  const organizationState = useOrganizationStore.getState();
  const organizations = Array.isArray(organizationState.organizations)
    ? organizationState.organizations
    : [];
  const matchingOrganization = organizations.find(
    (organization) => organization._id === organizationId,
  );

  if (matchingOrganization?.name) {
    return matchingOrganization.name;
  }

  if (
    organizationState.currentContext?.type === 'organization' &&
    organizationState.currentContext.id === organizationId
  ) {
    return organizationState.currentContext.displayName;
  }

  return undefined;
}

function mergeImportedSchemaPreview(
  discoveryResult: DataDiscoveryResult | null,
  importedSchema: SchemaPreview,
): DataDiscoveryResult {
  if (!discoveryResult) {
    return {
      totalSources: 1,
      suggestedQueries: [`Show ${importedSchema.displayName} data`],
      categories: [
        {
          name: importedSchema.organizationName ?? 'Imported CSV datasets',
          schemaCount: 1,
          schemas: [importedSchema],
        },
      ],
    };
  }

  let replacedExistingSchema = false;
  const categoryName = importedSchema.organizationName ?? 'Imported CSV datasets';
  let matchedCategory = false;

  const categories = discoveryResult.categories.map((category) => {
    if (category.name !== categoryName) {
      const hasExistingSchema = category.schemas.some((schema) => schema.id === importedSchema.id);
      if (!hasExistingSchema) {
        return category;
      }

      replacedExistingSchema = true;
      return {
        ...category,
        schemas: category.schemas.map((schema) => (schema.id === importedSchema.id ? importedSchema : schema)),
      };
    }

    matchedCategory = true;
    const existingIndex = category.schemas.findIndex((schema) => schema.id === importedSchema.id);
    const nextSchemas =
      existingIndex >= 0
        ? category.schemas.map((schema) => (schema.id === importedSchema.id ? importedSchema : schema))
        : [importedSchema, ...category.schemas];

    if (existingIndex >= 0) {
      replacedExistingSchema = true;
    }

    return {
      ...category,
      schemaCount: nextSchemas.length,
      schemas: nextSchemas,
    };
  });

  const nextCategories = matchedCategory
    ? categories
    : [
        {
          name: categoryName,
          schemaCount: 1,
          schemas: [importedSchema],
        },
        ...categories,
      ];

  return {
    ...discoveryResult,
    totalSources: replacedExistingSchema
      ? discoveryResult.totalSources
      : discoveryResult.totalSources + 1,
    categories: nextCategories,
  };
}

function findDiscoveredSchema(
  discoveryResult: DataDiscoveryResult | null,
  schemaId: string,
) {
  if (!discoveryResult) {
    return undefined;
  }

  for (const category of discoveryResult.categories) {
    const match = category.schemas.find((schema) => schema.id === schemaId);
    if (match) {
      return match;
    }
  }

  return undefined;
}

function getTrimmedSchemaId(value: string | null | undefined): string | undefined {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
}

function getSchemaIdCandidates(
  schema: SchemaExplorationResult['schema'],
  requestedSchemaId: string,
  selectedSchemaId: string | null,
): string[] {
  return Array.from(
    new Set(
      [
        getTrimmedSchemaId(schema.id),
        getTrimmedSchemaId(requestedSchemaId),
        getTrimmedSchemaId(selectedSchemaId),
      ].filter((candidate): candidate is string => candidate !== undefined),
    ),
  );
}

function findDiscoveredSchemaByIds(
  discoveryResult: DataDiscoveryResult | null,
  schemaIds: string[],
) {
  for (const candidateId of schemaIds) {
    const discoveredSchema = findDiscoveredSchema(discoveryResult, candidateId);
    if (discoveredSchema) {
      return discoveredSchema;
    }
  }

  return undefined;
}

function findImportedSchemaBindingByIds(schemaIds: string[]) {
  for (const candidateId of schemaIds) {
    const binding = getImportedSchemaBinding(candidateId);
    if (binding) {
      return {
        schemaId: candidateId,
        binding,
      };
    }
  }

  return undefined;
}

type QueryGroupByField = ChartQueryDefinition['groupBy'] | TemplateQuery['groupBy'];

function isTimeGranularity(value: string): value is TimeGranularity {
  switch (value) {
    case 'minute':
    case 'hour':
    case 'day':
    case 'week':
    case 'month':
      return true;
    default:
      return false;
  }
}

function isNumericSchemaField(field: SchemaFieldInfo): boolean {
  return field.type === 'number';
}

function isTimeSchemaField(field: SchemaFieldInfo): boolean {
  return field.type === 'date' || field.type === 'datetime';
}

function normalizeTemplateTimeRange(
  timeRange: TemplateQuery['timeRange'],
): ChartQueryDefinition['timeRange'] {
  if (!timeRange?.field) {
    return undefined;
  }

  return {
    field: timeRange.field,
    ...(timeRange.preset ? { preset: timeRange.preset } : {}),
    ...(timeRange.from ? { from: timeRange.from } : {}),
    ...(timeRange.to ? { to: timeRange.to } : {}),
  };
}

function normalizeTemplateGroupBy(
  groupBy: TemplateQuery['groupBy'],
): ChartQueryDefinition['groupBy'] {
  const normalizedGroupBy = groupBy?.reduce<NonNullable<ChartQueryDefinition['groupBy']>>((acc, entry) => {
    if (typeof entry === 'string') {
      acc.push({ field: entry });
      return acc;
    }

    acc.push({
      field: entry.field,
      ...(isTimeGranularity(entry.granularity) ? { granularity: entry.granularity } : {}),
    });
    return acc;
  }, []);

  return normalizedGroupBy && normalizedGroupBy.length > 0
    ? normalizedGroupBy
    : undefined;
}

function getMeasureValueField(field: string, alias?: string): string {
  return alias || field.split('.').pop() || field;
}

function getLinkedDataTextValueFields(
  measures: Array<{
    field: string;
    alias?: string;
  }>,
): string[] {
  const valueFields = [
    ...new Set(measures.map((measure) => getMeasureValueField(measure.field, measure.alias))),
  ];

  return valueFields.length > 0 ? valueFields : ['value'];
}

function getLinkedDataTextDateField(groupBy: QueryGroupByField): string {
  const firstGroupBy = groupBy?.[0];
  if (!firstGroupBy) {
    return 'timestamp';
  }

  const rawField = typeof firstGroupBy === 'string' ? firstGroupBy : firstGroupBy.field;
  return rawField.replace(/\./g, '_');
}

function getSelectedPinBindingTarget(
  component: Component,
): {
  pins: BindableMapPin[];
  pinIndex: number;
  pinName: string;
} | null {
  if (!isMapChartComponent(component) || typeof component.properties.selectedPinId !== 'string') {
    return null;
  }

  const pins = getComponentPins(component);
  const pinIndex = pins.findIndex((pin) => pin.id === component.properties.selectedPinId);
  if (pinIndex === -1) {
    return null;
  }

  return {
    pins,
    pinIndex,
    pinName: pins[pinIndex].name || 'pin',
  };
}

function createSingleSourcePinBindings(
  queryDefinition: ChartQueryDefinition,
  organizationId?: string,
  organizationName?: string,
  queryTemplateId?: string,
): PinDataBinding[] {
  const resolvedOrganizationId = organizationId ?? queryDefinition.organizationId;
  const resolvedOrganizationName = organizationName ?? queryDefinition.organizationName;

  return queryDefinition.measures.map((measure) => ({
    id: crypto.randomUUID(),
    dataSource: {
      type: 'query-template',
      ...getDataSourceOrganizationFields(resolvedOrganizationId, resolvedOrganizationName),
      queryDefinition: {
        ...queryDefinition,
        measures: [measure],
        ...getQueryOrganizationFields(resolvedOrganizationId, resolvedOrganizationName),
      },
    },
    ...(queryTemplateId ? { queryTemplateId } : {}),
    valueField: getMeasureValueField(measure.field, measure.alias),
    displayLabel: getMeasureValueField(measure.field, measure.alias),
  }));
}

export interface GeneratedQueryBindingSelection {
  queryDefinition: ChartQueryDefinition;
  organizationId?: string;
  organizationName?: string;
}

export interface UseDataDiscoveryReturn {
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  discoveryResult: DataDiscoveryResult | null;
  schemaExplorationResult: SchemaExplorationResult | null;
  openModal: () => Promise<void>;
  closeModal: () => void;
  handleSelectSchema: (schemaId: string, schemaName: string) => void;
  handleSelectTemplateField: (selection: TemplateFieldSelection) => void;
  handleSelectTemplate: (template: QueryTemplate) => void;
  handleApplyQueryDefinition: (selection: GeneratedQueryBindingSelection) => void;
  handleSuggestedQuery: (query: string) => void;
  handleUseSchema: (schemaId: string) => void;
  handleSelectWorkflow: (workflow: { id: string; name: string }) => void;
  handleSelectComposite: (selection: CompositeBindingSelection) => void;
  handleImportedCsvDatasetResult: (result: CsvDatasetImportResult) => void;
  handleBackToDiscovery: () => void;
}

export function useDataDiscovery({
  component,
  updateComponent,
  onMessage,
  onSetInput,
  onUpdateLinkedDataText,
}: UseDataDiscoveryOptions): UseDataDiscoveryReturn {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [discoveryResult, setDiscoveryResult] = useState<DataDiscoveryResult | null>(null);
  const [schemaExplorationResult, setSchemaExplorationResult] =
    useState<SchemaExplorationResult | null>(null);
  const [selectedSchemaId, setSelectedSchemaId] = useState<string | null>(null);

  const closeModal = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
    setError(null);
    setDiscoveryResult(null);
    setSchemaExplorationResult(null);
    setSelectedSchemaId(null);
  }, []);

  const openModal = useCallback(async () => {
    if (isLoading) return;
    setIsOpen(true);
    setIsLoading(true);
    setError(null);
    setDiscoveryResult(null);
    setSchemaExplorationResult(null);
    setSelectedSchemaId(null);
    try {
      const orgState = useOrganizationStore.getState();
      const isPersonalSpace = orgState.currentContext?.type === 'user';
      const result = isPersonalSpace
        ? await discoverCrossOrgDataSources()
        : await discoverDataSources();
      setDiscoveryResult(result);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to discover data sources'
      );
    } finally {
      setIsLoading(false);
    }
  }, [isLoading]);

  const handleSelectSchema = useCallback(
    async (schemaId: string, _schemaName: string) => {
      setIsLoading(true);
      try {
        const selectedSchema = findDiscoveredSchema(discoveryResult, schemaId);
        const orgConfig = selectedSchema?.organizationId
          ? { headers: { 'X-Organization-Id': selectedSchema.organizationId } }
          : undefined;
        const result = await exploreSchema(schemaId, orgConfig);
        setSchemaExplorationResult(result);
        setSelectedSchemaId(schemaId);
      } catch (err) {
        setError(
          `Failed to load schema details: ${err instanceof Error ? err.message : 'Unknown error'}`
        );
      } finally {
        setIsLoading(false);
      }
    },
    [discoveryResult]
  );

  const handleSelectTemplateField = useCallback(
    (selection: TemplateFieldSelection) => {
      if (!component) return;

      if (selection.compositeQueryDefinition) {
        const selectedField =
          selection.alias ||
          selection.fieldName.split('.').pop() ||
          selection.fieldName;
        const compositeDefinition = selection.compositeQueryDefinition;
        const xAxisKey = getXAxisKeyFromComposite(compositeDefinition);
        const selectedLineKeys = getCompositeLineKeysForComponent(
          component,
          compositeDefinition,
          selectedField,
          xAxisKey,
        );

        if (isMapChartComponent(component) && typeof component.properties.selectedPinId === 'string') {
          const pinId = component.properties.selectedPinId;
          const pins = getComponentPins(component);
          const pinIndex = pins.findIndex((pin) => pin.id === pinId);
          if (pinIndex === -1) return;

          const newBinding: PinDataBinding = {
            id: crypto.randomUUID(),
            dataSource: {
              type: 'composite-query',
              compositeQueryDefinition: compositeDefinition,
              ...getDataSourceOrganizationFields(selection.organizationId, selection.organizationName),
            },
            queryTemplateId: selection.templateId,
            valueField: selectedField,
            displayLabel: selectedField,
          };

          const updatedPins = [...pins];
          updatedPins[pinIndex] = {
            ...updatedPins[pinIndex],
            dataBindings: [
              ...getPinDataBindings(updatedPins[pinIndex]),
              newBinding,
            ],
          };

          updateComponent(component.id, {
            properties: { ...component.properties, pins: updatedPins },
          });

          const pinName = pins[pinIndex].name || 'pin';
          onMessage(
            `Bound **${selectedField}** from composite template "${selection.templateName}" to pin "${pinName}" tooltip.`
          );
          closeModal();
          return;
        }

        const typeUpdate =
          component.type === ComponentType.TEXT
            ? { type: ComponentType.DATA_TEXT }
            : {};

        updateComponent(component.id, {
          ...typeUpdate,
          properties: {
            ...component.properties,
            data: undefined,
            dataSource: {
              type: 'composite-query',
              compositeQueryDefinition: compositeDefinition,
              ...getDataSourceOrganizationFields(selection.organizationId, selection.organizationName),
            },
            queryTemplateId: selection.templateId,
            xAxisKey,
            lineKeys: selectedLineKeys,
            valueField: selectedField,
          },
        });

        onMessage(
          `Connected **${selectedField}** from composite template "${selection.templateName}".`
        );
        closeModal();
        return;
      }

      const aggregation = selection.aggregation;

      if (!aggregation) {
        toast.error('Selected template field is missing an aggregation.');
        return;
      }

      if (isMapChartComponent(component) && typeof component.properties.selectedPinId === 'string') {
        const pinId = component.properties.selectedPinId;
        const pins = getComponentPins(component);
        const pinIndex = pins.findIndex((pin) => pin.id === pinId);
        if (pinIndex === -1) return;

        const newBinding: PinDataBinding = {
          id: crypto.randomUUID(),
          dataSource: {
            type: 'query-template',
            ...getDataSourceOrganizationFields(selection.organizationId, selection.organizationName),
            queryDefinition: {
              schemaId: selection.schemaId,
              groupId: selection.groupId || '',
              schemaName: selection.templateName,
              measures: [
                { field: selection.fieldName, aggregation },
              ],
              ...getQueryOrganizationFields(selection.organizationId, selection.organizationName),
            },
          },
          queryTemplateId: selection.templateId,
          valueField:
            selection.alias ||
            selection.fieldName.split('.').pop() ||
            selection.fieldName,
          displayLabel:
            selection.alias ||
            selection.fieldName.split('.').pop() ||
            selection.fieldName,
        };

        const updatedPins = [...pins];
        updatedPins[pinIndex] = {
          ...updatedPins[pinIndex],
          dataBindings: [
            ...getPinDataBindings(updatedPins[pinIndex]),
            newBinding,
          ],
        };

        updateComponent(component.id, {
          properties: { ...component.properties, pins: updatedPins },
        });

        const pinName = pins[pinIndex].name || 'pin';
        onMessage(
          `Bound **${selection.alias || selection.fieldName}** (${aggregation}) to pin "${pinName}" tooltip.`
        );
        closeModal();
        return;
      }

      const typeUpdate =
        component.type === ComponentType.TEXT
          ? { type: ComponentType.DATA_TEXT }
          : {};

      updateComponent(component.id, {
        ...typeUpdate,
        properties: {
          ...component.properties,
          dataSource: {
            type: 'query-template',
            ...getDataSourceOrganizationFields(selection.organizationId, selection.organizationName),
            queryDefinition: {
              schemaId: selection.schemaId,
              groupId: selection.groupId || '',
              schemaName: selection.templateName,
              measures: [
                { field: selection.fieldName, aggregation },
              ],
              ...getQueryOrganizationFields(selection.organizationId, selection.organizationName),
            },
          },
          queryTemplateId: selection.templateId,
          valueField:
            selection.alias ||
            selection.fieldName.split('.').pop() ||
            selection.fieldName,
        },
      });

      onMessage(
        `Connected data to **${selection.alias || selection.fieldName}** (${aggregation}) from template "${selection.templateName}".`
      );
      closeModal();
    },
    [component, updateComponent, onMessage, closeModal]
  );

  const applySingleSourceQueryBinding = useCallback(
    (
      selection: GeneratedQueryBindingSelection,
      options: {
        message: string;
        queryTemplateId?: string;
        pinMessage?: (pinName: string, measureCount: number) => string;
      },
    ) => {
      if (!component) return;

      const { queryDefinition } = selection;
      const organizationId = selection.organizationId ?? queryDefinition.organizationId;
      const organizationName = selection.organizationName ?? queryDefinition.organizationName;

      const selectedPinTarget = getSelectedPinBindingTarget(component);
      if (selectedPinTarget) {
        const newBindings = createSingleSourcePinBindings(
          queryDefinition,
          organizationId,
          organizationName,
          options.queryTemplateId,
        );
        const updatedPins = [...selectedPinTarget.pins];
        updatedPins[selectedPinTarget.pinIndex] = {
          ...updatedPins[selectedPinTarget.pinIndex],
          dataBindings: [
            ...getPinDataBindings(updatedPins[selectedPinTarget.pinIndex]),
            ...newBindings,
          ],
        };

        updateComponent(component.id, {
          properties: { ...component.properties, pins: updatedPins },
        });

        onMessage(
          options.pinMessage?.(selectedPinTarget.pinName, newBindings.length) ??
            options.message,
        );
        closeModal();
        return;
      }

      const typeUpdate =
        component.type === ComponentType.TEXT
          ? { type: ComponentType.DATA_TEXT }
          : {};

      updateComponent(component.id, {
        ...typeUpdate,
        properties: {
          ...component.properties,
          data: undefined,
          lineKeys: undefined,
          xAxisKey: undefined,
          dataSource: {
            type: 'query-template',
            ...getDataSourceOrganizationFields(organizationId, organizationName),
            queryDefinition,
          },
          queryTemplateId: options.queryTemplateId,
        },
      });

      onUpdateLinkedDataText?.(
        component.id,
        getLinkedDataTextValueFields(queryDefinition.measures),
        getLinkedDataTextDateField(queryDefinition.groupBy),
      );

      onMessage(options.message);
      closeModal();
    },
    [component, updateComponent, onMessage, onUpdateLinkedDataText, closeModal],
  );

  const handleSelectTemplate = useCallback(
    (template: QueryTemplate) => {
      if (!component) return;

      if (template.templateType === 'composite-query' && template.compositeQueryDefinition) {
        const xAxisKey = getXAxisKeyFromComposite(template.compositeQueryDefinition);
        const lineKeys = getLineKeysFromComposite(template.compositeQueryDefinition);
        updateComponent(component.id, {
          properties: {
            ...component.properties,
            data: undefined,
            dataSource: {
              type: 'composite-query',
              compositeQueryDefinition: template.compositeQueryDefinition,
              ...getDataSourceOrganizationFields(template.organizationId),
            },
            queryTemplateId: template.id,
            xAxisKey,
            lineKeys,
          },
        });

        onUpdateLinkedDataText?.(component.id, lineKeys.length > 0 ? lineKeys : ['value'], xAxisKey);

        onMessage(
          `Applied composite template **"${template.name}"** to ${component.type.replace(/_/g, ' ').toLowerCase()}.`
        );
        closeModal();
        return;
      }

      const { query } = template;
      if (!query) return;

      const measures = (query.measures || []).map((measure) => ({
        field: measure.field,
        aggregation: measure.aggregation,
        ...(measure.alias ? { alias: measure.alias } : {}),
      }));

      applySingleSourceQueryBinding(
        {
          queryDefinition: {
            schemaId: query.schemaId,
            groupId: query.groupId || '',
            schemaName: template.name,
            measures,
            ...(normalizeTemplateTimeRange(query.timeRange)
              ? { timeRange: normalizeTemplateTimeRange(query.timeRange) }
              : {}),
            ...(normalizeTemplateGroupBy(query.groupBy)
              ? { groupBy: normalizeTemplateGroupBy(query.groupBy) }
              : {}),
            ...(query.filters ? { filters: query.filters } : {}),
            ...(query.orderBy ? { orderBy: query.orderBy } : {}),
            ...(typeof query.limit === 'number' ? { limit: query.limit } : {}),
            ...getQueryOrganizationFields(template.organizationId),
          },
          organizationId: template.organizationId,
        },
        {
          message: `Applied template **"${template.name}"** (${measures.length} measure${measures.length !== 1 ? 's' : ''}) to ${component.type.replace(/_/g, ' ').toLowerCase()}.`,
          queryTemplateId: template.id,
          pinMessage: (pinName, measureCount) =>
            `Applied template **"${template.name}"** (${measureCount} measure${measureCount !== 1 ? 's' : ''}) to pin "${pinName}".`,
        },
      );
    },
    [component, updateComponent, onMessage, onUpdateLinkedDataText, closeModal, applySingleSourceQueryBinding]
  );

  const handleApplyQueryDefinition = useCallback(
    (selection: GeneratedQueryBindingSelection) => {
      if (!component) return;

      applySingleSourceQueryBinding(selection, {
        message: `Applied generated template to ${component.type.replace(/[_-]/g, ' ').toLowerCase()}.`,
        pinMessage: (pinName, measureCount) =>
          `Applied generated template (${measureCount} measure${measureCount !== 1 ? 's' : ''}) to pin "${pinName}".`,
      });
    },
    [component, applySingleSourceQueryBinding],
  );

  const handleSuggestedQuery = useCallback(
    (query: string) => {
      onSetInput(query);
      closeModal();
    },
    [onSetInput, closeModal]
  );

  const handleUseSchema = useCallback(
    (schemaId: string) => {
      if (!component || !schemaExplorationResult) return;

      const schema = schemaExplorationResult.schema;
      const firstNumeric = schemaExplorationResult.chartableFields[0];
      const schemaIdCandidates = getSchemaIdCandidates(schema, schemaId, selectedSchemaId);
      const selectedSchema = findDiscoveredSchemaByIds(discoveryResult, schemaIdCandidates);
      const importedBindingMatch = findImportedSchemaBindingByIds(schemaIdCandidates);
      const importedBinding = importedBindingMatch?.binding;
      const selectedSchemaOrganizationId =
        typeof selectedSchema?.organizationId === 'string' &&
        selectedSchema.organizationId.trim().length > 0
          ? selectedSchema.organizationId
          : undefined;
      const resolvedSchemaId =
        selectedSchema?.id ||
        importedBindingMatch?.schemaId ||
        schemaIdCandidates[0] ||
        schemaId;
      const groupId = schema.groupId || selectedSchema?.groupId || importedBinding?.groupId;
      const organizationId = selectedSchemaOrganizationId ?? importedBinding?.organizationId;
      const organizationName = selectedSchema?.organizationName;
      const isTableBinding = isTableComponentType(component.type);
      const tableFields = isTableBinding
        ? getSchemaFieldsForTabularBinding(schemaExplorationResult)
        : [];
      const tableMeasures = createRawTableMeasures(tableFields);
      const tableColumns = createTableColumnsFromSchemaFields(tableFields);
      const valueField = firstNumeric?.name || tableFields[0]?.name || 'value';

      if (!groupId) {
        toast.error('Selected schema is missing its group binding. Reopen the schema from the data modal and try again.');
        return;
      }

      const typeUpdate =
        component.type === ComponentType.TEXT
          ? { type: ComponentType.DATA_TEXT }
          : {};
      const measures = isTableBinding && tableMeasures.length > 0
        ? tableMeasures
        : [
            {
              field: firstNumeric?.name || 'value',
              aggregation: 'last' as const,
            },
          ];

      updateComponent(component.id, {
        ...typeUpdate,
        properties: {
          ...component.properties,
          data: undefined,
          ...(isTableBinding && tableColumns.length > 0 ? { columns: tableColumns } : {}),
          dataSource: {
            type: 'query-template',
            ...getDataSourceOrganizationFields(organizationId, organizationName),
            queryDefinition: {
              schemaId: resolvedSchemaId,
              groupId,
              schemaName: schema.displayName || schema.name,
              ...getQueryOrganizationFields(organizationId, organizationName),
              measures,
              ...(isTableBinding ? { limit: 100 } : {}),
            },
          },
          queryTemplateId: undefined,
          valueField,
        },
      });

      onMessage(
        `Connected data to **${schema.displayName || schema.name}** -> ${firstNumeric?.displayName || firstNumeric?.name || valueField}.`
      );
      closeModal();
    },
    [component, schemaExplorationResult, discoveryResult, selectedSchemaId, updateComponent, onMessage, closeModal]
  );

  const handleSelectWorkflow = useCallback(
    (workflow: { id: string; name: string }) => {
      if (!component) return;

      const typeUpdate =
        component.type === ComponentType.TEXT
          ? { type: ComponentType.DATA_TEXT }
          : {};

      const workflowDataSource = {
        type: 'workflow' as const,
        workflowConfig: {
          workflowId: workflow.id,
          workflowName: workflow.name,
        },
      };

      const isDataCardOrDataText =
        component.type === ComponentType.DATA_TEXT ||
        component.type === ComponentType.DATA_CARD ||
        component.type === ComponentType.TEXT ||
        component.properties?.useDataText === true;

      void (async () => {
        try {
          const { traceClient } = await import('@/services/tracing/TraceClient');
          const thread = await traceClient.createThread('ui_action', `Add Data: workflow -> ${component.id}`);
          if (thread) {
            const spanId = await traceClient.startSpan(thread.threadId, 'ui_action', `Select workflow for ${component.id}`, {
              detail: {
                action: 'handleSelectWorkflow',
                componentId: component.id,
                componentType: component.type,
                isDataCardOrDataText,
                willSetValueDataSource: isDataCardOrDataText,
                workflowId: workflow.id,
                workflowName: workflow.name,
                propertiesBeingSet: {
                  dataSource: true,
                  valueDataSource: isDataCardOrDataText,
                  useDataText: isDataCardOrDataText,
                },
              },
            });
            if (spanId) await traceClient.endSpan(spanId, 'ok');
          }
        } catch {
          // fire-and-forget
        }
      })();

      updateComponent(component.id, {
        ...typeUpdate,
        properties: {
          ...component.properties,
          dataSource: workflowDataSource,
          ...(isDataCardOrDataText ? {
            valueDataSource: workflowDataSource,
            useDataText: true,
          } : {}),
        },
      });

      onMessage(`Connected to workflow **${workflow.name}** as data source.`);
      closeModal();
    },
    [component, updateComponent, onMessage, closeModal]
  );

  const handleSelectComposite = useCallback(
    (selection: CompositeBindingSelection) => {
      if (!component) return;

      const orgState = useOrganizationStore.getState();
      const isPersonalSpace = orgState.currentContext?.type === 'user';
      const ownerValidationError = validateCompositeDefinitionSourceOwners(
        selection.definition,
      );
      if (ownerValidationError) {
        toast.error(ownerValidationError);
        return;
      }
      const resolvedScope = getCompositeDefinitionOrganizationScope(selection.definition);
      if (
        selection.organizationId &&
        resolvedScope &&
        selection.organizationId !== resolvedScope.organizationId
      ) {
        toast.error(COMPOSITE_SOURCE_OWNER_MISMATCH_ERROR);
        return;
      }

      const organizationId = resolvedScope?.organizationId;
      const organizationName = resolvedScope?.organizationName ?? selection.organizationName;

      if (isPersonalSpace && !organizationId) {
        toast.error(PERSONAL_SPACE_COMPOSITE_SCOPE_ERROR);
        return;
      }

      const xAxisKey = getXAxisKeyFromComposite(selection.definition);
      const lineKeys = getLineKeysFromComposite(selection.definition);
      updateComponent(component.id, {
        properties: {
          ...component.properties,
          dataSource: {
            type: 'composite-query' as const,
            compositeQueryDefinition: selection.definition,
            ...getDataSourceOrganizationFields(organizationId, organizationName),
          },
          queryTemplateId: undefined,
          xAxisKey,
          lineKeys,
        },
      });
      onUpdateLinkedDataText?.(component.id, lineKeys.length > 0 ? lineKeys : ['value'], xAxisKey);
      onMessage(
        `Connected ${selection.definition.sources.length} data sources with a ${selection.definition.joinType} join.`
      );
      closeModal();
    },
    [component, updateComponent, onMessage, onUpdateLinkedDataText, closeModal]
  );

  const handleImportedCsvDatasetResult = useCallback(
    (result: CsvDatasetImportResult) => {
      const displayName = result.candidate.labels.schemaDisplayName;
      const schemaName = result.candidate.labels.schemaName;
      const organizationId = result.source.dataOwnerOrganizationId;
      const organizationName = resolveOrganizationName(organizationId);
      const fieldInfos: SchemaFieldInfo[] = result.preview.columns.map((column) => ({
        name: column.fieldName,
        displayName: column.headerLabel,
        type: column.type,
        isRequired: column.required,
      }));
      const chartableFields = fieldInfos.filter(isNumericSchemaField);
      const timeFields = fieldInfos.filter(isTimeSchemaField);
      const otherFields = fieldInfos.filter(
        (field) => !isNumericSchemaField(field) && !isTimeSchemaField(field),
      );
      const importedSchema: SchemaPreview = {
        id: result.source.schemaId,
        name: schemaName,
        displayName,
        description: `Imported CSV dataset for ${displayName}.`,
        groupId: result.source.groupId,
        fieldCount: fieldInfos.length,
        numericFieldCount: chartableFields.length,
        timeFieldCount: timeFields.length,
        hasTimeFields: timeFields.length > 0,
        fields: fieldInfos.map((field) => field.name),
        organizationId,
        organizationName,
      };

      setError(null);
      setIsLoading(false);
      setSelectedSchemaId(result.source.schemaId);
      setDiscoveryResult((previous) => mergeImportedSchemaPreview(previous, importedSchema));
      setSchemaExplorationResult({
        schema: {
          id: result.source.schemaId,
          name: schemaName,
          displayName,
          description: importedSchema.description,
          groupId: result.source.groupId,
          fields: fieldInfos,
          timeFields: timeFields.map((field) => field.name),
          numericFields: chartableFields.map((field) => field.name),
        },
        chartableFields,
        timeFields,
        otherFields,
        sampleQueries: [
          `Show ${chartableFields[0]?.displayName || chartableFields[0]?.name || displayName} from ${displayName}`,
          `Get the latest ${displayName} reading`,
        ],
      });
    },
    [],
  );

  const handleBackToDiscovery = useCallback(() => {
    setSchemaExplorationResult(null);
    setSelectedSchemaId(null);
  }, []);

  return {
    isOpen,
    isLoading,
    error,
    discoveryResult,
    schemaExplorationResult,
    openModal,
    closeModal,
    handleSelectSchema,
    handleSelectTemplateField,
    handleSelectTemplate,
    handleApplyQueryDefinition,
    handleSuggestedQuery,
    handleUseSchema,
    handleSelectWorkflow,
    handleSelectComposite,
    handleImportedCsvDatasetResult,
    handleBackToDiscovery,
  };
}
